import { useCallback, useMemo, useState } from 'react';
import type { ArtifactKind } from '@inference.engine/widgets';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { FolderInputIcon, UploadIcon } from 'lucide-react';
import { projectAttachedArtifactIdsQuery } from '@/entities/project-artifact';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useAttachArtifacts } from '@/features/manage-project-artifacts';
import type { AddArtifactsModalProps } from './AddArtifactsModal.types';
import { ExistingArtifactsPicker } from './ExistingArtifactsPicker';
import { UploadView, useUploadQueue } from './UploadView';
import { useTranslation } from 'react-i18next';

type Step = 'entry' | 'upload' | 'picker';

export function AddArtifactsModal(props: AddArtifactsModalProps) {
  const { t } = useTranslation('projects');

  // The host (`useModalState`) unmounts this component on close, so step +
  // picker + upload state reset for free on re-open.
  const [step, setStep] = useState<Step>('entry');
  // Picker state lives here so Back → Browse preserves filters / sort /
  // selection.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [kindFilter, setKindFilter] = useState<ArtifactKind | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<string | null>(null);

  const workspaceId = useCurrentWorkspaceId();

  const { onClose } = props;
  // `onClose` alone — the host unmounts on its own timer. See `useModalState`.
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Queue lifted so the footer reads its state directly — no controller-effect
  // round-trip from UploadView.
  const uploadQueue = useUploadQueue({
    projectId: props.projectId,
    onAllSucceeded: handleClose,
  });

  const { mutate: attachMutate, isPending: isAttachPending } = useAttachArtifacts({
    projectId: props.projectId,
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleClose();
  };

  const isEntry = step === 'entry';
  const isUpload = step === 'upload';
  const isPicker = step === 'picker';

  // Picker now consumes `attachedIds` as a prop (Option B from the lift to
  // `entities/artifact`) so it stays free of cross-entity value imports.
  // The host (this modal) issues the cross-entity read and projects the
  // raw id list into the `Set<string>` shape the picker expects. `enabled:
  // isPicker` defers the call until the user is actually on the picker
  // step — preserves the previous "fetch on demand" behavior.
  const { data: attachedIdsList } = useQuery({
    ...projectAttachedArtifactIdsQuery(props.projectId),
    enabled: isPicker,
  });
  const attachedIds = useMemo(() => new Set(attachedIdsList ?? []), [attachedIdsList]);

  const cancelLabel = isEntry
    ? t('projectDetail.addArtifactsModal.cancel')
    : t('projectDetail.addArtifactsModal.back');
  const handleCancel = () => {
    if (isEntry) {
      handleClose();
      return;
    }
    // Back from Upload resets the queue per AC; picker preserves selection.
    if (isUpload) {
      uploadQueue.reset();
    }
    setStep('entry');
  };

  // Disable Back only while an upload batch is in flight.
  const cancelDisabled = isUpload && uploadQueue.isAnyUploading;

  const handleAttachPicker = () => {
    if (selectedIds.size === 0) return;
    const attachments = Array.from(selectedIds).map((artifactId) => ({ artifactId }));
    attachMutate(
      { attachments },
      {
        // Clear on either outcome so reopen starts clean and retries don't
        // resend the same set.
        onSuccess: () => {
          setSelectedIds(new Set());
          handleClose();
        },
        onError: () => {
          setSelectedIds(new Set());
        },
      },
    );
  };

  let addCount = 0;
  let addDisabled = true;
  let isSubmitting = false;
  let handleAddClick: (() => void) | undefined;
  let addLabel: string = t('projectDetail.addArtifactsModal.add');
  if (isPicker) {
    addCount = selectedIds.size;
    addDisabled = selectedIds.size === 0 || isAttachPending;
    isSubmitting = isAttachPending;
    handleAddClick = handleAttachPicker;
    addLabel =
      addCount > 0
        ? t('projectDetail.addArtifactsModal.addWithCount', { addCount })
        : t('projectDetail.addArtifactsModal.add');
  } else if (isUpload) {
    addCount = uploadQueue.submittableChips.length;
    addDisabled =
      uploadQueue.submittableChips.length === 0 ||
      uploadQueue.isAnyUploading ||
      uploadQueue.isPending;
    isSubmitting = uploadQueue.isAnyUploading || uploadQueue.isPending;
    handleAddClick = uploadQueue.submit;
    if (isSubmitting) {
      addLabel = t('projectDetail.addArtifactsModal.uploading');
    } else if (uploadQueue.hasErrors) {
      addLabel = t('projectDetail.addArtifactsModal.retryWithCount', { addCount });
    } else {
      addLabel =
        addCount > 0
          ? t('projectDetail.addArtifactsModal.addWithCount', { addCount })
          : t('projectDetail.addArtifactsModal.add');
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={clsx(
          'gap-0 p-4 w-full max-w-203.25 max-h-[90vh] flex flex-col',
          // Picker needs a stable tall canvas for sticky table headers; upload
          // grows from a comfortable floor as chips stack so the dropzone never
          // jumps but a long queue can still scroll inside the chip area.
          isPicker && 'h-[850px]',
          isUpload && 'min-h-[450px]',
        )}
        onPointerDownOutside={(event) => {
          // Notistack toasts render in a body-level portal; clicks on them count
          // as pointer-down-outside and would otherwise close the dialog.
          const target = event.target as HTMLElement | null;
          if (target?.closest('[class*="notistack"]')) {
            event.preventDefault();
          }
        }}
        onOpenAutoFocus={(event) => {
          // Focus the dialog root instead of the first card — programmatic
          // .focus() on a button doesn't trigger :focus-visible, so the ring
          // would land on Browse after the first Tab. Focusing root keeps the
          // focus trap and lets Tab move the visible ring onto Upload first.
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {isEntry && t('projectDetail.addArtifactsModal.selectArtifacts')}
            {isUpload && t('projectDetail.addArtifactsModal.uploadDocuments')}
            {isPicker && t('projectDetail.addArtifactsModal.addExistingArtifacts')}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-1 min-h-0 flex-col gap-4 py-4">
          {isEntry && (
            <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto">
              <div className="grid grid-cols-1 gap-4 p-1 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setStep('upload')}
                  className="flex flex-col items-center gap-3 rounded-lg border border-border p-8 text-center transition-colors hover:border-primary hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <div className="rounded-md bg-accent p-2">
                    <UploadIcon className="size-5 text-primary" />
                  </div>
                  <span className="text-base font-medium">
                    {t('projectDetail.addArtifactsModal.uploadDocuments2')}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t('projectDetail.addArtifactsModal.fromYourComputer')}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStep('picker')}
                  className="flex flex-col items-center gap-3 rounded-lg border border-border p-8 text-center transition-colors hover:border-primary hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <div className="rounded-md bg-accent p-2">
                    <FolderInputIcon className="size-5 text-primary" />
                  </div>
                  <span className="text-base font-medium">
                    {t('projectDetail.addArtifactsModal.addArtifactsFromWorkspace')}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t('projectDetail.addArtifactsModal.importArtifacts')}
                  </span>
                </button>
              </div>
            </div>
          )}

          {isUpload && (
            <UploadView
              chips={uploadQueue.chips}
              warnings={uploadQueue.warnings}
              isAnyUploading={uploadQueue.isAnyUploading}
              isPending={uploadQueue.isPending}
              onDrop={uploadQueue.handleDrop}
              onRemove={uploadQueue.handleRemove}
              onDismissWarning={uploadQueue.dismissWarning}
            />
          )}

          {isPicker &&
            (workspaceId ? (
              <ExistingArtifactsPicker
                workspaceId={workspaceId}
                attachedIds={attachedIds}
                selectedIds={selectedIds}
                kindFilter={kindFilter}
                searchInput={searchInput}
                sort={sort}
                isUserInput={false}
                onSelectedIdsChange={setSelectedIds}
                onKindFilterChange={setKindFilter}
                onSearchInputChange={setSearchInput}
                onSortChange={setSort}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('projectDetail.addArtifactsModal.selectAWorkspaceToContinue')}
              </p>
            ))}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={cancelDisabled}>
            {cancelLabel}
          </Button>
          {!isEntry && (
            <Button disabled={addDisabled} onClick={handleAddClick}>
              {isSubmitting && <Spinner aria-hidden />}
              {addLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
